import {
  Color,
  DoubleSide,
  Euler,
  InstancedMesh,
  Matrix4,
  PlaneGeometry,
  Quaternion,
  ShaderMaterial,
  Uniform,
  Vector3,
} from "three";
import {
  DEFAULT_UNDERWATER_VISUAL_SETTINGS,
  type UnderwaterVisualSettings,
} from "./VisualSettings";

function randomRange(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function randomSigned() {
  return Math.random() * 2 - 1;
}

type SeaweedInstanceData = {
  x: number;
  y: number;
  z: number;
  height: number;
  width: number;
  quaternion: Quaternion;
};

const vertexShader = /* glsl */ `
  uniform float time;
  uniform float seaweedHeight;
  uniform float seaweedWidth;
  uniform float seaweedSway;

  varying vec2 vSeaweedUv;
  varying vec3 vSeaweedWorldPosition;
  varying float vSeaweedSeed;

  float seaweedHash(float value) {
    return fract(sin(value * 127.1) * 43758.5453123);
  }

  void main() {
    vSeaweedUv = uv;

    float instanceId = float(gl_InstanceID);
    float seed = seaweedHash(instanceId + 1.0);
    float seed2 = seaweedHash(instanceId + 19.0);
    float y01 = clamp(uv.y, 0.0, 1.0);
    float baseHold = smoothstep(0.0, 0.14, y01);
    float taper = pow(max(1.0 - y01, 0.0), 1.35);
    float widthProfile = mix(0.48, 1.0, baseHold) * mix(0.08, 1.0, taper);
    float bend = y01 * y01 * (0.55 + seed * 0.65);
    float localPhase = seed * 6.28318530718;
    float slowCurrent = sin(time * (0.55 + seed * 0.35) + localPhase);
    float highCurrent = sin(time * (1.35 + seed2 * 0.75) + y01 * 5.2 + localPhase);

    vec3 transformed = position;
    transformed.x *= widthProfile * seaweedWidth;
    transformed.y *= seaweedHeight;
    transformed.x += (slowCurrent * 0.13 + highCurrent * 0.038) * bend * seaweedSway;
    transformed.z += cos(time * 0.42 + localPhase + y01 * 3.4) * 0.075 * bend * seaweedSway;
    transformed.x += sin(y01 * 7.8 + localPhase) * 0.018 * bend;

    mat4 instanceTransform = mat4(1.0);
    #ifdef USE_INSTANCING
      instanceTransform = instanceMatrix;
    #endif

    vec4 worldPosition = modelMatrix * instanceTransform * vec4(transformed, 1.0);
    vSeaweedWorldPosition = worldPosition.xyz;
    vSeaweedSeed = seed;

    gl_Position = projectionMatrix * viewMatrix * worldPosition;
  }
`;

const fragmentShader = /* glsl */ `
  uniform float time;
  uniform float roomHeight;
  uniform float baseCameraHeight;
  uniform float seaweedTipLight;
  uniform float seaweedDepthFade;
  uniform vec3 seaweedColor;
  uniform vec3 seaweedTipColor;

  varying vec2 vSeaweedUv;
  varying vec3 vSeaweedWorldPosition;
  varying float vSeaweedSeed;

  void main() {
    float y01 = clamp(vSeaweedUv.y, 0.0, 1.0);
    float edge = abs(vSeaweedUv.x - 0.5) * 2.0;
    float edgeAlpha = 1.0 - smoothstep(0.72, 1.0, edge);
    float rootAlpha = smoothstep(0.0, 0.09, y01);
    float tipAlpha = 1.0 - smoothstep(0.88, 1.0, y01) * 0.32;

    float fineStripe = sin(y01 * 44.0 + vSeaweedSeed * 12.0) * 0.5 + 0.5;
    float vein = 1.0 - smoothstep(0.0, 0.24, edge);
    float verticalDepth = smoothstep(
      -baseCameraHeight - 0.36,
      roomHeight * 0.34 - baseCameraHeight,
      vSeaweedWorldPosition.y
    );
    float waterFade = mix(1.0, 0.54 + verticalDepth * 0.46, seaweedDepthFade);
    float causticPulse = sin(
      vSeaweedWorldPosition.x * 4.2 +
      vSeaweedWorldPosition.z * 3.1 +
      time * 0.72 +
      vSeaweedSeed * 8.0
    ) * 0.5 + 0.5;
    float bladeVariation = 0.86 + vSeaweedSeed * 0.22;

    vec3 rootColor = seaweedColor * vec3(0.2, 0.38, 0.36);
    vec3 midColor = seaweedColor * bladeVariation;
    vec3 tipColor = seaweedTipColor * seaweedTipLight;
    vec3 waterTint = vec3(0.12, 0.36, 0.38);
    vec3 bladeColor = mix(rootColor, midColor, smoothstep(0.04, 0.58, y01));
    bladeColor = mix(bladeColor, tipColor, smoothstep(0.56, 1.0, y01));
    bladeColor += vec3(0.03, 0.09, 0.075) * vein * (0.45 + y01);
    bladeColor += vec3(0.025, 0.07, 0.06) * fineStripe * y01 * 0.28;
    bladeColor += vec3(0.06, 0.16, 0.14) * causticPulse * y01 * 0.18;
    bladeColor = mix(waterTint, bladeColor, waterFade);

    float alpha = edgeAlpha * rootAlpha * tipAlpha * (0.72 + y01 * 0.22);
    float dither = fract(
      52.9829189 *
      fract(dot(gl_FragCoord.xy + vSeaweedSeed * 31.7, vec2(0.06711056, 0.00583715)))
    );
    if (alpha < 0.035 || alpha < dither * 0.42) discard;

    gl_FragColor = vec4(bladeColor, 1.0);
  }
`;

export class SeaWeeds extends InstancedMesh {
  private readonly maxCount: number;
  private readonly instanceData: SeaweedInstanceData[] = [];
  private readonly tmpMatrix = new Matrix4();
  private readonly tmpPosition = new Vector3();
  private readonly tmpScale = new Vector3();
  private readonly timeUniform: Uniform<number>;
  private readonly heightUniform: Uniform<number>;
  private readonly widthUniform: Uniform<number>;
  private readonly swayUniform: Uniform<number>;
  private readonly tipLightUniform: Uniform<number>;
  private readonly depthFadeUniform: Uniform<number>;
  private readonly colorUniform: Uniform<Color>;
  private readonly tipColorUniform: Uniform<Color>;
  private currentArea = Number.NaN;

  constructor(
    roomSize: number,
    roomHeight: number,
    baseCameraHeight: number,
    count: number
  ) {
    const geometry = new PlaneGeometry(1, 1, 5, 18);
    geometry.translate(0, 0.5, 0);

    const timeUniform = new Uniform(0);
    const heightUniform = new Uniform(
      DEFAULT_UNDERWATER_VISUAL_SETTINGS.seaweedHeight
    );
    const widthUniform = new Uniform(
      DEFAULT_UNDERWATER_VISUAL_SETTINGS.seaweedWidth
    );
    const swayUniform = new Uniform(
      DEFAULT_UNDERWATER_VISUAL_SETTINGS.seaweedSway
    );
    const tipLightUniform = new Uniform(
      DEFAULT_UNDERWATER_VISUAL_SETTINGS.seaweedTipLight
    );
    const depthFadeUniform = new Uniform(
      DEFAULT_UNDERWATER_VISUAL_SETTINGS.seaweedDepthFade
    );
    const colorUniform = new Uniform(
      new Color(DEFAULT_UNDERWATER_VISUAL_SETTINGS.seaweedColor)
    );
    const tipColorUniform = new Uniform(
      new Color(DEFAULT_UNDERWATER_VISUAL_SETTINGS.seaweedTipColor)
    );

    const material = new ShaderMaterial({
      side: DoubleSide,
      transparent: false,
      depthWrite: true,
      depthTest: true,
      alphaTest: 0.015,
      uniforms: {
        time: timeUniform,
        roomHeight: new Uniform(roomHeight),
        baseCameraHeight: new Uniform(baseCameraHeight),
        seaweedHeight: heightUniform,
        seaweedWidth: widthUniform,
        seaweedSway: swayUniform,
        seaweedTipLight: tipLightUniform,
        seaweedDepthFade: depthFadeUniform,
        seaweedColor: colorUniform,
        seaweedTipColor: tipColorUniform,
      },
      vertexShader,
      fragmentShader,
    });

    super(geometry, material, count);

    this.maxCount = count;
    this.timeUniform = timeUniform;
    this.heightUniform = heightUniform;
    this.widthUniform = widthUniform;
    this.swayUniform = swayUniform;
    this.tipLightUniform = tipLightUniform;
    this.depthFadeUniform = depthFadeUniform;
    this.colorUniform = colorUniform;
    this.tipColorUniform = tipColorUniform;
    this.name = "seaweeds";
    this.frustumCulled = false;

    const clusterCount = Math.max(22, Math.floor(count / 9));
    const clusterCenters = Array.from({ length: clusterCount }, () => {
      const radius = Math.sqrt(Math.random()) * (roomSize * 0.48);
      const angle = Math.random() * Math.PI * 2;
      return new Vector3(Math.cos(angle) * radius, 0, Math.sin(angle) * radius);
    });

    const position = new Vector3();
    const quaternion = new Quaternion();
    const euler = new Euler();

    for (let i = 0; i < count; i += 1) {
      const center = clusterCenters[i % clusterCenters.length];
      const clusterRadius = randomRange(0.04, 0.34);
      const offsetRadius = Math.pow(Math.random(), 1.65) * clusterRadius;
      const offsetAngle = Math.random() * Math.PI * 2;
      const rootJitter = randomRange(-0.018, 0.025);
      const height = randomRange(roomHeight * 0.18, roomHeight * 0.43);
      const width = randomRange(0.028, 0.09);
      const yaw = Math.random() * Math.PI * 2;
      const lean = randomRange(0.02, 0.2);

      position.set(
        center.x + Math.cos(offsetAngle) * offsetRadius,
        rootJitter,
        center.z + Math.sin(offsetAngle) * offsetRadius
      );
      euler.set(lean * randomSigned(), yaw, lean * randomSigned());
      quaternion.setFromEuler(euler);
      this.instanceData.push({
        x: position.x,
        y: position.y,
        z: position.z,
        height,
        width,
        quaternion: quaternion.clone(),
      });
    }

    this.applyArea(DEFAULT_UNDERWATER_VISUAL_SETTINGS.seaweedArea);
  }

  setSettings(settings: UnderwaterVisualSettings) {
    const count = Math.round(settings.seaweedCount);
    this.count = Math.max(0, Math.min(this.maxCount, count));
    this.visible = this.count > 0;
    this.applyArea(settings.seaweedArea);
    this.heightUniform.value = settings.seaweedHeight;
    this.widthUniform.value = settings.seaweedWidth;
    this.swayUniform.value = settings.seaweedSway;
    this.tipLightUniform.value = settings.seaweedTipLight;
    this.depthFadeUniform.value = settings.seaweedDepthFade;
    this.colorUniform.value.set(settings.seaweedColor);
    this.tipColorUniform.value.set(settings.seaweedTipColor);
  }

  private applyArea(area: number) {
    const clampedArea = Math.max(0.05, area);
    if (Math.abs(this.currentArea - clampedArea) < 0.001) {
      return;
    }

    for (let i = 0; i < this.instanceData.length; i += 1) {
      const data = this.instanceData[i];
      this.tmpPosition.set(
        data.x * clampedArea,
        data.y,
        data.z * clampedArea
      );
      this.tmpScale.set(data.width, data.height, data.width);
      this.tmpMatrix.compose(
        this.tmpPosition,
        data.quaternion,
        this.tmpScale
      );
      this.setMatrixAt(i, this.tmpMatrix);
    }

    this.instanceMatrix.needsUpdate = true;
    this.currentArea = clampedArea;
  }

  update(time: number) {
    this.timeUniform.value = time;
  }
}
